import {
  DeleteOutlineRounded,
  KeyboardArrowDownRounded,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  ListItem,
  MenuItem,
  Select,
  Stack,
  Typography,
  type SelectChangeEvent,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHyperTable } from "../../../HyperTable";
import type {
  ActiveFilter,
  HyperTableCustomFilter,
  HyperTableFilterBoth,
  RenderFilterProps,
} from "../../../filter-types";

interface CustomFilterProps<Entity> {
  filter: HyperTableCustomFilter<Entity> &
    HyperTableFilterBoth<Entity> &
    ActiveFilter<Entity>;
  closeMenu: VoidFunction;
}

const CustomFilter = <Entity extends any>(props: CustomFilterProps<Entity>) => {
  const { filter, closeMenu } = props;
  const ctx = useHyperTable<Entity>();
  const { filterActions: activeFiltersActions } = ctx;
  const [selectValue, setSelectValue] = useState<string | undefined>(
    filter.meta.selectOperatorValue ?? filter.select?.initialValue,
  );

  const onSave = useCallback<RenderFilterProps<Entity>["onSave"]>(
    (updater, options = {}) => {
      const { closeOnSave } = options;

      activeFiltersActions.update(filter.id, (prev) => {
        const { meta, value } =
          updater instanceof Function
            ? updater({ meta: prev.meta, value: prev.value })
            : updater;

        return {
          meta: { ...meta, selectOperatorValue: selectValue },
          value,
          id: filter.id,
          saveInSessionStorage: filter.saveInSessionStorage,
        };
      });

      if (closeOnSave) {
        closeMenu();
      }
    },
    [
      activeFiltersActions.update,
      filter.id,
      filter.saveInSessionStorage,
      selectValue,
    ],
  );

  function handleSelectChange(e: SelectChangeEvent<string>) {
    const value = e.target.value;
    setSelectValue(value);
  }

  useEffect(() => {
    filter.select?.onChange({
      context: ctx,
      meta: filter.meta,
      value: filter.value,
      onSave,
      selectValue,
    });
  }, [selectValue]);

  const RenderFilter = filter.renderFilter;
  const filterBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      const input = filterBodyRef.current?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), textarea',
      );
      input?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frameId);
  }, []);

  return (
    <>
      <ListItem sx={{ justifyContent: "space-between", pb: "0.6vh" }}>
        <Typography fontWeight="600" color="secondary">
          {filter.caption}
        </Typography>
        <Stack direction="row" alignItems="center" gap="1vw">
          {filter.select && (
            <Select
              value={selectValue}
              IconComponent={KeyboardArrowDownRounded}
              onChange={handleSelectChange}
              color="secondary"
              sx={{
                minWidth: "6rem",
                height: "2rem",
                borderRadius: "1rem",
                fontSize: "0.9rem",
              }}
            >
              {filter.select.options.map(({ caption, value }) => (
                <MenuItem value={value} key={value}>
                  {caption}
                </MenuItem>
              ))}
            </Select>
          )}
          <IconButton
            color="primary"
            aria-label="Remove filter"
            onClick={() => {
              activeFiltersActions.remove(filter.id);
              closeMenu();
            }}
          >
            <DeleteOutlineRounded aria-hidden="true" />
          </IconButton>
        </Stack>
      </ListItem>
      <Divider sx={{ mb: "1vh" }} />
      <Box ref={filterBodyRef} sx={{ pb: 0.5 }}>
        <RenderFilter
          context={ctx}
          meta={filter.meta}
          value={filter.value}
          onSave={onSave}
          selectValue={selectValue}
        />
      </Box>
    </>
  );
};

export default CustomFilter;
